import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDataProvider } from "react-admin";

export const AutocompleteField = ({
	value,
	onChange,
	resource,
	filterKey,
	perPage = 10,
	label,
	filters = {},
	sort = {},
	distinct = null,
	groupBy = null,
	include = [],
	getOptionLabel = (option) => option?.uid,
	getOptionValue = (option) => option?.uid,
	isOptionEqualToValue = (option, value) => option?.uid === value,
	searchOnType = false,
}) => {
	const dataProvider = useDataProvider();
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [inputValue, setInputValue] = useState("");
	const fetchTimeoutRef = useRef(null); // Used to debounce search

	const fetchOptions = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await dataProvider.getList(resource, {
				filter: {
					...filters,
					...(searchOnType && { prefixSearch: true }),
				},
				pagination: { page: 1, perPage: perPage },
				include: include,
				sort: sort,
				distinct: distinct,
				...(searchOnType && inputValue && { search: inputValue }),
			});
			if (data) {
				setOptions(data);
			}
			setLoading(false);
		} catch (error) {
			console.log(error);
			setLoading(false);
		}
	}, [
		dataProvider,
		resource,
		JSON.stringify(filters),
		perPage,
		include,
		sort,
		distinct,
		inputValue,
	]);

	useEffect(() => {
		fetchOptions();
	}, [resource]);

	useEffect(() => {
		if (Object.keys(filters).includes(filterKey)) {
			handleInputChange({
				hash: filters[filterKey],
			});
		}
	}, [JSON.stringify(filters)]);

	useEffect(() => {
		if (searchOnType) {
			if (fetchTimeoutRef.current) {
				// Cancel any pending request
				clearTimeout(fetchTimeoutRef.current);
			}

			fetchTimeoutRef.current = setTimeout(() => fetchOptions(), 300);

			return () => {
				if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
			};
		}
	}, [inputValue]);

	const handleChange = useCallback(
		(_, newValue) => {
			const selectedValue = getOptionValue(newValue);
			setInputValue(getOptionLabel(newValue));
			onChange?.(selectedValue);
		},
		[onChange],
	);

	const getFilterOptions = () => {
		if (searchOnType) {
			// Return options as-is (no client filtering)
			return (options) => options;
		}
		// Use default filtering behavior
		return undefined;
	};

	const handleInputChange = (_, value) => {
		setInputValue(value);
	};

	return (
		<Autocomplete
			sx={{ width: 300 }}
			size="small"
			options={options}
			filterOptions={getFilterOptions()}
			value={value}
			inputValue={inputValue}
			onInputChange={handleInputChange}
			loading={loading}
			onChange={handleChange}
			groupBy={groupBy}
			getOptionLabel={getOptionLabel}
			isOptionEqualToValue={isOptionEqualToValue}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					slotProps={{
						input: {
							...params.InputProps,
							endAdornment: (
								<React.Fragment>
									{loading ? (
										<CircularProgress color="inherit" size={20} />
									) : null}
									{params.InputProps.endAdornment}
								</React.Fragment>
							),
						},
					}}
				/>
			)}
		/>
	);
};
